import { useEffect, useState } from "react";
import { useAuth } from "../../lib/auth";
import { tareasApi, api } from "../../lib/api";
import {
  Plus, X, CheckCircle2, Trash2, Bell, Clock, ChevronDown, ChevronUp,
  PartyPopper, PlayCircle, CheckCheck, Loader2, MessageSquare,
} from "lucide-react";
import { Link } from "react-router-dom";
import { eventosApi } from "../../lib/api";

interface UsuarioActivo {
  id: string;
  fullName: string;
  email: string;
}

interface Tarea {
  id: number;
  titulo: string;
  descripcion?: string;
  prioridad: string;
  estado: string;
  creadoPorId: string;
  creadoPorNombre: string;
  destinatarios: Array<{ id: string; nombre: string }>;
  fechaLimite?: string;
  completadaAt?: string;
  createdAt: string;
}

const PRIORIDAD_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  urgente: { label: "Urgente", bg: "bg-red-100",    text: "text-red-700",    dot: "bg-red-500" },
  alta:    { label: "Alta",    bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-400" },
  normal:  { label: "Normal",  bg: "bg-blue-100",   text: "text-blue-700",   dot: "bg-blue-500" },
  baja:    { label: "Baja",    bg: "bg-slate-100",  text: "text-slate-600",  dot: "bg-slate-400" },
};

function fmtFecha(iso?: string) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString("es-VE", { day: "numeric", month: "short", year: "numeric" });
}

function fmtFechaHora(iso?: string) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString("es-VE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function isVencida(fechaLimite?: string) {
  if (!fechaLimite) return false;
  return new Date(fechaLimite) < new Date();
}

export default function TareasPage() {
  const { user } = useAuth();
  const userId = (user as any)?.sub ?? (user as any)?.id ?? user?.id;

  const [tareas, setTareas] = useState<Tarea[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioActivo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [prioridad, setPrioridad] = useState("normal");
  const [selectedDests, setSelectedDests] = useState<string[]>([]);
  const [fechaLimite, setFechaLimite] = useState("");
  const [showDestinatarios, setShowDestinatarios] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitulo, setEditTitulo] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editPrioridad, setEditPrioridad] = useState("normal");
  const [editFecha, setEditFecha] = useState("");
  const [editDests, setEditDests] = useState<string[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Section collapse
  const [showCompletadas, setShowCompletadas] = useState(false);

  // Tareas del checklist de eventos asignadas al usuario
  interface TareaEvento {
    id: number; nombre: string; fecha: string; horaInicio: string; sucursalId: number; estado: string;
    itemId: string; itemText: string; itemEstado: string; fechaEntrega?: string;
    observacion?: string; completadoPor?: string; completadoAt?: string;
  }
  const [tareasEventos, setTareasEventos] = useState<TareaEvento[]>([]);
  const [loadingEv, setLoadingEv] = useState(true);
  const [updatingItem, setUpdatingItem] = useState<string | null>(null);

  // Modal de observación al cambiar estado
  const [obsModal, setObsModal] = useState<{ tarea: TareaEvento; nuevoEstado: string } | null>(null);
  const [obsTexto, setObsTexto] = useState("");

  function loadTareas() {
    setLoading(true);
    tareasApi.list()
      .then(t => setTareas(Array.isArray(t) ? t : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  function loadTareasEventos() {
    setLoadingEv(true);
    eventosApi.misTareas()
      .then((t: any) => setTareasEventos(Array.isArray(t) ? t : []))
      .catch(() => setTareasEventos([]))
      .finally(() => setLoadingEv(false));
  }

  function abrirCambioEstado(t: TareaEvento, nuevoEstado: string) {
    setObsModal({ tarea: t, nuevoEstado });
    setObsTexto("");
  }

  async function confirmarCambioEstado() {
    if (!obsModal) return;
    const { tarea: t, nuevoEstado } = obsModal;
    const key = `${t.id}_${t.itemId}`;
    setUpdatingItem(key);
    try {
      await eventosApi.updateChecklistItem(t.id, {
        itemId: t.itemId,
        estado: nuevoEstado,
        observacion: obsTexto.trim() || undefined,
        ...(nuevoEstado === "culminado" ? { completadoPor: user?.fullName ?? user?.email ?? "—" } : {}),
      });
      setObsModal(null);
      loadTareasEventos();
    } finally { setUpdatingItem(null); }
  }

  useEffect(() => {
    loadTareas();
    loadTareasEventos();
    api<UsuarioActivo[]>("/v1/users/activos")
      .then(u => setUsuarios(Array.isArray(u) ? u : []))
      .catch(() => setUsuarios([]));
  }, []);

  function toggleDest(id: string) {
    setSelectedDests(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  function resetForm() {
    setTitulo("");
    setDescripcion("");
    setPrioridad("normal");
    setSelectedDests([]);
    setFechaLimite("");
    setFormError(null);
    setShowDestinatarios(false);
  }

  async function handleCrear(e: React.FormEvent) {
    e.preventDefault();
    if (!titulo.trim()) { setFormError("El título es obligatorio"); return; }
    setSaving(true);
    setFormError(null);
    try {
      const dests = selectedDests.map(id => {
        const u = usuarios.find(u => u.id === id);
        return { id, nombre: u?.fullName ?? u?.email ?? id };
      });
      await tareasApi.crear({
        titulo: titulo.trim(),
        descripcion: descripcion.trim() || undefined,
        prioridad,
        destinatarios: dests,
        fechaLimite: fechaLimite || undefined,
      });
      resetForm();
      setShowForm(false);
      loadTareas();
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleCompletar(id: number) {
    try {
      await tareasApi.completar(id);
      loadTareas();
    } catch (e: any) {
      alert(e.message);
    }
  }

  function startEdit(t: Tarea) {
    setEditingId(t.id);
    setEditTitulo(t.titulo);
    setEditDesc(t.descripcion ?? "");
    setEditPrioridad(t.prioridad);
    setEditFecha(t.fechaLimite ? t.fechaLimite.slice(0, 16) : "");
    setEditDests(t.destinatarios.map(d => d.id));
    setEditError(null);
  }
  function cancelEdit() { setEditingId(null); setEditError(null); }

  async function guardarEdit() {
    if (!editTitulo.trim()) { setEditError("El título es requerido"); return; }
    setEditSaving(true); setEditError(null);
    try {
      const dests = usuarios
        .filter(u => editDests.includes(u.id))
        .map(u => ({ id: u.id, nombre: u.fullName }));
      await tareasApi.actualizar(editingId!, {
        titulo: editTitulo,
        descripcion: editDesc || undefined,
        prioridad: editPrioridad,
        destinatarios: dests,
        fechaLimite: editFecha || null,
      });
      setEditingId(null);
      loadTareas();
    } catch (e: any) {
      setEditError(e.message);
    } finally { setEditSaving(false); }
  }

  async function handleReabrir(id: number) {
    try {
      await tareasApi.reabrir(id);
      loadTareas();
    } catch (e: any) { alert(e.message); }
  }

  async function handleEliminar(id: number) {
    if (!confirm("¿Eliminar esta tarea?")) return;
    try {
      await tareasApi.eliminar(id);
      loadTareas();
    } catch (e: any) {
      alert(e.message);
    }
  }

  const pendientes = tareas.filter(t => t.estado === "pendiente");
  const completadas = tareas.filter(t => t.estado === "completada");

  function canCompletar(t: Tarea) {
    return t.creadoPorId === userId || t.destinatarios.some(d => d.id === userId);
  }

  function canEditar(t: Tarea) {
    return t.creadoPorId === userId || ['admin','gerencia'].includes(user?.role ?? '');
  }
  function canEliminar(t: Tarea) {
    return t.creadoPorId === userId || ['admin','gerencia'].includes(user?.role ?? '');
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-100">
            <Bell className="size-5 text-violet-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink-900">Avisos y Tareas</h1>
            <p className="text-sm text-ink-400">Recordatorios y tareas operativas del equipo</p>
          </div>
        </div>
        <button
          onClick={() => { setShowForm(true); resetForm(); }}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-colors"
        >
          <Plus className="size-4" />
          Nueva Tarea
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="mb-6 bg-white rounded-2xl border border-violet-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-violet-100 bg-violet-50">
            <span className="font-semibold text-violet-800">Nueva tarea operativa</span>
            <button onClick={() => { setShowForm(false); resetForm(); }} className="text-ink-400 hover:text-ink-700">
              <X className="size-4" />
            </button>
          </div>
          <form onSubmit={handleCrear} className="p-5 space-y-4">
            {/* Título */}
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Título <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={titulo}
                onChange={e => setTitulo(e.target.value)}
                placeholder="Ej: Reponer stock de servilletas"
                className="w-full px-3 py-2 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                autoFocus
              />
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Descripción (opcional)</label>
              <textarea
                value={descripcion}
                onChange={e => setDescripcion(e.target.value)}
                rows={2}
                placeholder="Detalles adicionales..."
                className="w-full px-3 py-2 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Prioridad */}
              <div>
                <label className="block text-xs font-medium text-ink-600 mb-1">Prioridad</label>
                <select
                  value={prioridad}
                  onChange={e => setPrioridad(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
                >
                  <option value="baja">Baja</option>
                  <option value="normal">Normal</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>

              {/* Fecha límite */}
              <div>
                <label className="block text-xs font-medium text-ink-600 mb-1">Fecha límite (opcional)</label>
                <input
                  type="datetime-local"
                  value={fechaLimite}
                  onChange={e => setFechaLimite(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>

            {/* Destinatarios */}
            <div>
              <button
                type="button"
                onClick={() => setShowDestinatarios(v => !v)}
                className="flex items-center gap-2 text-xs font-medium text-ink-600 hover:text-violet-700 transition-colors"
              >
                {showDestinatarios ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
                Asignar a compañeros
                {selectedDests.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs">{selectedDests.length} seleccionados</span>
                )}
              </button>
              {showDestinatarios && (
                <div className="mt-2 border border-ink-200 rounded-lg overflow-hidden max-h-44 overflow-y-auto">
                  {usuarios.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-ink-400">Cargando usuarios...</p>
                  ) : (
                    usuarios.map(u => (
                      <label key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-violet-50 cursor-pointer border-b border-ink-100 last:border-0">
                        <input
                          type="checkbox"
                          checked={selectedDests.includes(u.id)}
                          onChange={() => toggleDest(u.id)}
                          className="accent-violet-600"
                        />
                        <span className="text-sm text-ink-800">{u.fullName}</span>
                        <span className="text-xs text-ink-400 ml-auto">{u.email}</span>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>

            {formError && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{formError}</p>
            )}

            <div className="flex items-center justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={() => { setShowForm(false); resetForm(); }}
                className="px-4 py-2 text-sm text-ink-600 hover:text-ink-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50 transition-colors"
              >
                {saving ? "Guardando..." : "Crear tarea"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Loading / Error */}
      {loading && (
        <div className="text-center py-12 text-ink-400 text-sm">Cargando tareas...</div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 mb-4">{error}</div>
      )}

      {/* Modal de observación al cambiar estado */}
      {obsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-5 py-4 border-b border-ink-100 bg-indigo-50">
              <h3 className="font-semibold text-indigo-800">
                {obsModal.nuevoEstado === "culminado" ? "Culminar tarea" : obsModal.nuevoEstado === "en_proceso" ? "Iniciar tarea" : "Cambiar estado"}
              </h3>
              <p className="text-xs text-ink-500 mt-0.5 truncate">{obsModal.tarea.itemText}</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-ink-600 mb-1">Observación (opcional)</label>
                <textarea
                  value={obsTexto}
                  onChange={e => setObsTexto(e.target.value)}
                  rows={3}
                  placeholder="Ej: Se completó la decoración del salón principal..."
                  className="w-full px-3 py-2 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
                  autoFocus
                />
              </div>
              <p className="text-xs text-ink-400">
                Fecha de registro: <strong>{new Date().toLocaleDateString("es-VE", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</strong>
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setObsModal(null)}
                  className="px-4 py-2 text-sm text-ink-600 hover:text-ink-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmarCambioEstado}
                  disabled={updatingItem !== null}
                  className={`px-5 py-2 rounded-xl text-white text-sm font-medium transition-colors disabled:opacity-50 ${
                    obsModal.nuevoEstado === "culminado"
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {updatingItem ? "Guardando..." : obsModal.nuevoEstado === "culminado" ? "Culminar" : "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tareas del checklist de eventos asignadas a mí */}
      {!loadingEv && tareasEventos.length > 0 && (
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <PartyPopper className="size-4 text-indigo-500" aria-hidden />
            <h2 className="text-sm font-semibold text-indigo-700 uppercase tracking-wide">Tareas de eventos</h2>
            <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700">{tareasEventos.length}</span>
          </div>
          <ul className="space-y-2">
            {tareasEventos.map(t => {
              const key = `${t.id}_${t.itemId}`;
              const enProceso = t.itemEstado === "en_proceso";
              const updating = updatingItem === key;
              const fechaEv = new Date(t.fecha + "T00:00:00").toLocaleDateString("es-VE", { weekday: "short", day: "numeric", month: "short" });
              return (
                <li key={key} className="bg-white rounded-xl border border-indigo-100 p-3 sm:p-4">
                  <div className="flex items-start gap-3">
                    <div className="size-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                      <PartyPopper size={16} aria-hidden />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-ink-800 mb-0.5 truncate">{t.itemText}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-ink-500">
                        <Link to="/tienda/eventos" className="text-indigo-600 hover:underline truncate max-w-[160px]">
                          {t.nombre}
                        </Link>
                        <span className="inline-flex items-center gap-1">
                          <Clock size={11} aria-hidden /> {fechaEv} · {t.horaInicio?.slice(0,5)}
                        </span>
                        {t.fechaEntrega && (
                          <span className="text-amber-700">entregar: {t.fechaEntrega}</span>
                        )}
                      </div>
                      {enProceso && (
                        <span className="inline-flex items-center gap-1 mt-1.5 text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">
                          <PlayCircle size={11} aria-hidden /> En proceso
                        </span>
                      )}
                      {t.observacion && (
                        <p className="mt-1.5 text-xs text-ink-500 flex items-start gap-1">
                          <MessageSquare size={11} className="shrink-0 mt-0.5 text-ink-400" aria-hidden />
                          {t.observacion}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {!enProceso && (
                        <button
                          onClick={() => abrirCambioEstado(t, "en_proceso")}
                          disabled={updating}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50">
                          {updating ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} aria-hidden />}
                          En proceso
                        </button>
                      )}
                      <button
                        onClick={() => abrirCambioEstado(t, "culminado")}
                        disabled={updating}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-semibold">
                        {updating ? <Loader2 size={12} className="animate-spin" /> : <CheckCheck size={12} aria-hidden />}
                        Culminar
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Pendientes */}
      {!loading && (
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold text-ink-700 uppercase tracking-wide">Pendientes</h2>
            <span className="px-2 py-0.5 rounded-full text-xs bg-ink-100 text-ink-600">{pendientes.length}</span>
          </div>

          {pendientes.length === 0 ? (
            <div className="bg-white rounded-2xl border border-ink-100 px-5 py-8 text-center">
              <CheckCircle2 className="size-8 text-green-400 mx-auto mb-2" />
              <p className="text-sm text-ink-500">Sin tareas pendientes — ¡todo al día!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendientes.map(t => {
                const pc = PRIORIDAD_CONFIG[t.prioridad] ?? PRIORIDAD_CONFIG.normal;
                const vencida = isVencida(t.fechaLimite);
                return (
                  <div key={t.id} className={`bg-white rounded-2xl border overflow-hidden ${vencida ? "border-red-200" : "border-ink-100"}`}>
                    <div className="flex items-start gap-3 px-4 py-3.5">
                      <div className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${pc.dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="font-semibold text-ink-900 text-sm">{t.titulo}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${pc.bg} ${pc.text}`}>{pc.label}</span>
                          {vencida && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600 flex items-center gap-1">
                              <Clock className="size-3" /> Vencida
                            </span>
                          )}
                        </div>
                        {t.descripcion && (
                          <p className="text-sm text-ink-500 mt-0.5 leading-snug">{t.descripcion}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          <span className="text-xs text-ink-400">De: <span className="font-medium text-ink-600">{t.creadoPorNombre}</span></span>
                          {t.fechaLimite && (
                            <span className={`text-xs flex items-center gap-1 ${vencida ? "text-red-500 font-medium" : "text-ink-400"}`}>
                              <Clock className="size-3" />
                              Límite: {fmtFechaHora(t.fechaLimite)}
                            </span>
                          )}
                        </div>
                        {t.destinatarios && t.destinatarios.length > 0 && (
                          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                            <span className="text-xs text-ink-400">Para:</span>
                            {t.destinatarios.map(d => (
                              <span key={d.id} className="px-2 py-0.5 text-xs rounded-full bg-violet-100 text-violet-700 font-medium">{d.nombre}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        {canEditar(t) && (
                          <button
                            onClick={() => startEdit(t)}
                            title="Editar"
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-ink-50 text-ink-600 hover:bg-brand-50 hover:text-brand-700 transition-colors border border-ink-200"
                          >
                            ✏️ Editar
                          </button>
                        )}
                        {canCompletar(t) && (
                          <button
                            onClick={() => handleCompletar(t.id)}
                            title="Marcar como completada"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 transition-colors border border-green-200"
                          >
                            <CheckCircle2 className="size-3.5" />
                            Completar
                          </button>
                        )}
                        {canEliminar(t) && (
                          <button
                            onClick={() => handleEliminar(t.id)}
                            title="Eliminar tarea"
                            className="p-1.5 rounded-lg text-ink-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Formulario de edición inline */}
                    {editingId === t.id && (
                      <div className="mx-4 mb-3 p-4 bg-violet-50 rounded-xl border border-violet-200 space-y-3">
                        {editError && <p className="text-xs text-red-600">{editError}</p>}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-ink-500 mb-1">Título *</label>
                            <input className="input w-full text-sm" value={editTitulo}
                              onChange={e => setEditTitulo(e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-xs text-ink-500 mb-1">Prioridad</label>
                            <select className="input w-full text-sm" value={editPrioridad}
                              onChange={e => setEditPrioridad(e.target.value)}>
                              <option value="baja">Baja</option>
                              <option value="normal">Normal</option>
                              <option value="alta">Alta</option>
                              <option value="urgente">Urgente</option>
                            </select>
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-xs text-ink-500 mb-1">Descripción</label>
                            <textarea className="input w-full text-sm resize-none" rows={2}
                              value={editDesc} onChange={e => setEditDesc(e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-xs text-ink-500 mb-1">Fecha límite</label>
                            <input type="datetime-local" className="input w-full text-sm" value={editFecha}
                              onChange={e => setEditFecha(e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-xs text-ink-500 mb-1">Destinatarios</label>
                            <div className="border border-ink-200 rounded-lg p-2 max-h-28 overflow-y-auto space-y-1 bg-white">
                              {usuarios.map(u => (
                                <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-ink-50 rounded px-1">
                                  <input type="checkbox"
                                    checked={editDests.includes(u.id)}
                                    onChange={e => setEditDests(prev => e.target.checked ? [...prev, u.id] : prev.filter(x => x !== u.id))}
                                    className="w-3 h-3 accent-violet-600" />
                                  <span className="text-xs text-ink-700">{u.fullName}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={guardarEdit} disabled={editSaving}
                            className="btn-primary text-xs px-4 disabled:opacity-50">
                            {editSaving ? "Guardando…" : "✓ Guardar cambios"}
                          </button>
                          <button onClick={cancelEdit} className="btn-secondary text-xs px-3">Cancelar</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Completadas */}
      {!loading && completadas.length > 0 && (
        <section>
          <button
            onClick={() => setShowCompletadas(v => !v)}
            className="flex items-center gap-2 mb-3 text-sm font-semibold text-ink-400 hover:text-ink-600 transition-colors"
          >
            {showCompletadas ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
            Completadas
            <span className="px-2 py-0.5 rounded-full text-xs bg-ink-100 text-ink-500">{completadas.length}</span>
          </button>

          {showCompletadas && (
            <div className="space-y-2">
              {completadas.map(t => {
                const pc = PRIORIDAD_CONFIG[t.prioridad] ?? PRIORIDAD_CONFIG.normal;
                return (
                  <div key={t.id} className="bg-white rounded-xl border border-ink-100 overflow-hidden opacity-70">
                    <div className="flex items-start gap-3 px-4 py-3">
                      <CheckCircle2 className="size-4 text-green-500 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-ink-700 text-sm line-through">{t.titulo}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${pc.bg} ${pc.text}`}>{pc.label}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          <span className="text-xs text-ink-400">De: {t.creadoPorNombre}</span>
                          {t.completadaAt && (
                            <span className="text-xs text-green-600">✓ {fmtFecha(t.completadaAt)}</span>
                          )}
                        </div>
                      </div>
                      {canEditar(t) && (
                        <button
                          onClick={() => handleReabrir(t.id)}
                          title="Reabrir tarea"
                          className="text-xs px-2 py-1 rounded-lg text-ink-400 hover:text-violet-600 hover:bg-violet-50 transition-colors border border-ink-200 shrink-0"
                        >↺ Reabrir</button>
                      )}
                      {canEliminar(t) && (
                        <button
                          onClick={() => handleEliminar(t.id)}
                          title="Eliminar"
                          className="p-1.5 rounded-lg text-ink-300 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
